<template>
    <v-flex>
        <GmapAutocomplete
            class="autocomplete-textfield"
            :value="record.name"
            @place_changed="place_changed"
            @input.native="checkEmpty"
            placeholder=""
            :disabled="isSearchDisabled">
        </GmapAutocomplete>
        <em class="autocomplete-textfield_error" v-if="requiredFieldError && isRecordNameEmpty">{{requiredFieldError}}</em>
    </v-flex>
</template>

<script>
    import { allCountriesInfo } from '../../common/utils/countryCodes';

    export default {
        name: 'GoogleSearch',
        data: () => ({
            isRecordNameEmpty: false,
            isSearch: true,
            searchResult: {
                name: null,
                website: null,
                city: null,
                country: null,
                streetAddress: null,
                zip: null,
                telephone: null,
                telephoneCountryCode: null,
                state: null,
                latitude: null,
                longitude: null,
            },
            countryName: '',
        }),
        props: {
            record: {
                type: Object,
                required: false,
            },
            requiredFieldError: {
                type: String,
                required: false,
            },
            isSearchDisabled: {
                type: Boolean,
                required: false,
            },
        },
        methods: {
            place_changed(data) {
                this.searchResult = {
                    address1: null,
                };
                this.filterSearchResults(data);
                this.$emit('place_changed', this.searchResult);
            },
            checkEmpty(event) {
                if (event && !event.currentTarget.value) {
                    this.filterSearchResults(event.data);
                    this.$emit('place_changed', this.searchResult);
                    this.isRecordNameEmpty = true;
                } else {
                    this.record.name = event.currentTarget.value;
                    this.isRecordNameEmpty = false;
                }
            },
            filterSearchResults(rawSearchResult) {
                if (rawSearchResult) {
                    let countryCode = '';
                    let telephone = '';
                    if (rawSearchResult.address_components && rawSearchResult.address_components.length > 0) {
                        let subLocality1 = '';
                        let subLocality2 = '';
                        let subLocality3 = '';
                        let streetNumber = '';
                        let route = '';
                        let streetAddress = '';
                        let neighborhood = '';

                        rawSearchResult.address_components.forEach((x) => {
                            if (x.types.includes('street_number')) {
                                streetNumber = x.long_name;
                            }
                            if (x.types.includes('route')) {
                                route = x.long_name;
                            }
                            if (x.types.includes('neighborhood')) {
                                neighborhood = x.long_name;
                            }
                            if (x.types.includes('sublocality_level_3')) {
                                subLocality3 = x.long_name;
                            }
                            if (x.types.includes('sublocality_level_2')) {
                                subLocality2 = x.long_name;
                            }
                            if (x.types.includes('sublocality_level_1')) {
                                subLocality1 = x.long_name;
                            }
                            if (x.types.includes('locality') && !this.searchResult.city) {
                                this.searchResult.city = x.long_name;
                            }
                            if (x.types.includes('administrative_area_level_2') && !this.searchResult.city) {
                                this.searchResult.city = x.long_name;
                            }
                            if (x.types.includes('country')) {
                                this.searchResult.country = x.long_name;
                                this.countryName = x.short_name;
                            }
                            if (x.types.includes('postal_code')) {
                                this.searchResult.zip = x.long_name;
                            }
                            if (x.types.includes('administrative_area_level_1')) {
                                this.searchResult.state = x.long_name;
                            }
                        });
                        if (streetNumber || route) {
                            this.searchResult.address1 = `${streetNumber} ${route}`;
                        }
                        if (subLocality1 || subLocality2 || subLocality3) {
                            streetAddress =
                                // eslint-disable-next-line no-nested-ternary
                                subLocality3 !== '' ?
                                `${subLocality3}, ${subLocality2}, ${subLocality1}` :
                                subLocality2 !== '' ?
                                `${subLocality2}, ${subLocality1}` :
                                `${subLocality1}`;
                                if (!this.searchResult.address1) {
                                    this.searchResult.address1 = streetAddress;
                                } else {
                                    this.searchResult.address2 = streetAddress;
                                }
                        }
                        if (neighborhood !== "") {
                            if (!this.searchResult.address1) {
                                this.searchResult.address1 = neighborhood;
                            } else if (!this.searchResult.address2) {
                                this.searchResult.address2 = neighborhood;
                            }
                        }

                        this.searchResult.latitude = rawSearchResult.geometry.location.lat();
                        this.searchResult.longitude = rawSearchResult.geometry.location.lng();
                    }
                    if (rawSearchResult.name) {
                        this.searchResult.name = rawSearchResult.name;
                    }
                    if (rawSearchResult.website) {
                        this.searchResult.websiteUrl = rawSearchResult.website;
                    }
                    if (rawSearchResult.international_phone_number) {
                        const telephoneNumber = rawSearchResult.international_phone_number.split(' ');
                        if (telephoneNumber.length > 0) {
                            if (telephoneNumber[0].charAt(0) === '+') {
                                countryCode = telephoneNumber[0].substr(1);
                                telephone = telephoneNumber.filter(x => x !== telephoneNumber[0]).join('');
                            }
                        }
                        this.searchResult.telephoneCountryCode = countryCode;
                        this.searchResult.telephone = telephone;
                    }
                    if (rawSearchResult.formatted_phone_number && telephone === "") {
                        telephone = rawSearchResult.formatted_phone_number;
                        if (telephone.charAt(0) === '0') {
                            this.searchResult.telephone = telephone.substr(1);
                        } else if (telephone.charAt(0) === '(' && telephone.charAt(1) === '0') {
                            this.searchResult.telephone = `(${telephone.substr(2)}`;
                        }
                        if (!this.searchResult.telephoneCountryCode && this.countryName !== '') {
                            const countryData = allCountriesInfo.find(x => x.iso2.toLowerCase() === this.countryName.toLowerCase());
                            if (countryData) {
                                this.searchResult.telephoneCountryCode = countryData.dialCode;
                            }
                        }
                    }
                } else {
                    this.searchResult = {};
                    this.isSearch = false;
                }
            },
        },
    };
</script>

<style lang="scss">
    @import "../../common/styles/_colors.scss";
    @import "../../common/styles/_responsive.scss";

    .autocomplete-textfield {
        &_error {
            font-size: 12px;
            color: $ypo-error-red;
            position: relative;
            top: -3px;
            font-style: normal;
        }
    }
</style>
